<script lang="ts">
export const jumpToFieldAttrs = (propertyId: string) => ({ 'data-field-property-id': propertyId })
</script>

<script setup lang="ts">
import CrumbItem from '@/modules/Workspaces/Crumbs/CrumbItem.vue'
import { invariant } from '@/shared/utils/typeAssertions'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import Menu from '@/uiKit/Menu'
import { computed, nextTick, ref, watch } from 'vue'

const props = defineProps<{ properties: { id: string; name: string }[] }>()

const jumpedToPropertyId = ref<string | null>(null)
watch(
  () => props.properties,
  () => {
    jumpedToPropertyId.value = props.properties.at(0)?.id || null
  },
  { immediate: true },
)

let highlightNode: HTMLDivElement | null = null
let waitTimeout = -1
let transitionTimeout = -1
let removeTimeout = -1

const scrollToField = async (propertyId: string) => {
  if (highlightNode) {
    // Reset the existing animation, as we want only one at a time.
    // A reset and restart here, allows the UI to feel responsive
    // if the user is spamming the functionality.
    highlightNode.remove()
    window.clearTimeout(waitTimeout)
    window.clearTimeout(transitionTimeout)
    window.clearTimeout(removeTimeout)
  }

  highlightNode = document.createElement('div')

  const field = document.querySelector<HTMLElement>(`[data-field-property-id="${propertyId}"]`)

  invariant(
    field,
    'Field element not found when jumping to field in the sidebar. The components are not properly configured.',
  )

  field.scrollIntoView({ behavior: 'smooth' })

  // Naive check to see if the field is in view.
  // We don't really care about accuracy,
  // as long as the element is in he viewport, this is good enough.
  // The goal is to decide how long to wait before showing the blue flash over the element,
  // so the user actually sees it.
  const fieldRect = field.getBoundingClientRect()
  const bodyRect = document.body.getBoundingClientRect()
  const isInView = fieldRect.top > bodyRect.top && fieldRect.top < bodyRect.bottom

  const TRANSITION_DURATION = 300
  const scrollWait = isInView ? 0 : 300

  highlightNode.classList.add(
    'transition-all',
    'absolute',
    'bg-background-transparent',
    'opacity-0',
  )
  highlightNode.style.transitionDuration = `${scrollWait}ms`

  const hasOwnRelative = field.classList.contains('relative')
  if (!hasOwnRelative) field.classList.add('relative')

  field.appendChild(highlightNode)
  await nextTick() // Wait for rerender to happen so the highlight node is in the DOM

  // In case the field is out of view, wait a bit for it to scroll in,
  // then update styles to trigger animation
  waitTimeout = window.setTimeout(() => {
    highlightNode?.classList.remove('bg-background-transparent', 'opacity-0')
    highlightNode?.classList.add('inset-0', 'bg-background-selected', 'opacity-100')
  }, scrollWait)

  // Wait for the transition to finish, then update styles again to trigger the reverse animation
  transitionTimeout = window.setTimeout(() => {
    highlightNode?.classList.remove('bg-background-selected', 'opacity-100')
    highlightNode?.classList.add('bg-background-transparent', 'opacity-0')
  }, scrollWait + TRANSITION_DURATION)

  // Once the reverse animation is finished,
  // remove the highlight node and restore the original styles of the field
  removeTimeout = window.setTimeout(
    () => {
      if (!hasOwnRelative) field.classList.remove('relative')
      if (!highlightNode) return
      highlightNode.remove()
      highlightNode = null
    },
    scrollWait + TRANSITION_DURATION * 2,
  )
}

const jumpToField = (propertyId: string) => {
  jumpedToPropertyId.value = propertyId
  scrollToField(propertyId)
}

const jumpedToProperty = computed(() =>
  props.properties.find((p) => p.id === jumpedToPropertyId.value),
)
</script>

<template>
  <CrumbItem v-if="properties.length === 0"> No properties </CrumbItem>
  <Menu.Root
    v-else
    v-slot="{ getTriggerProps }"
    close-on-select
    :positioning="{ offset: { mainAxis: 4 } }"
  >
    <DarwinButton
      v-if="jumpedToProperty"
      variant="transparent"
      size="xs"
      class="outline-none hover:bg-background-transparent-hovered focus-visible:shadow-focus-ring-primary"
      v-bind="{ ...getTriggerProps(), ...$attrs }"
      @click="jumpToField(jumpedToProperty.id)"
    >
      <div class="max-w-40 truncate">
        {{ jumpedToProperty.name }}
      </div>
      <template #trailing-icon>
        <IconSprite
          size="sm"
          variant="transparent"
          icon="chevron-down"
          class="shrink-0"
        />
      </template>
    </DarwinButton>

    <Menu.Content>
      <Menu.Item
        v-for="property in properties"
        :key="property.id"
        role="link"
        @select="jumpToField(property.id)"
      >
        <template #default="{ id }">
          <div
            :id="id"
            class="max-w-40 truncate"
          >
            {{ property.name }}
          </div>
        </template>
      </Menu.Item>
    </Menu.Content>
  </Menu.Root>
</template>
